import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { TableBaseAbstractComponent } from '../../shared/components/table-base-abstract.component';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { TableRequestData } from '../../shared/models/table/table-request-param';
import { Table, TableModule } from 'primeng/table';
import { PostApi } from '../../shared/services/api/post.api';
import { DatePipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Button } from 'primeng/button';
import { InboxType } from '../../shared/enums/inbox-type.enum';
import { DialogService } from 'primeng/dynamicdialog';
import { DocumentDetailsComponent } from './components/document-details/document-details.component';
import { Dokument } from '../../shared/models/dokument';
import { Card } from 'primeng/card';
import { PreviewDataWrapperComponent } from '../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { PreviewDataType } from '../../shared/enums/preview-data-type.enum';
import { Checkbox } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { TableResponse } from '../../shared/models/table-response';
import { InputText } from 'primeng/inputtext';
import { DatePicker } from 'primeng/datepicker';
import { DocumentStatus } from '../../shared/enums/document-status.enum';
import { ErrorCode } from '../../shared/enums/error-code.enum';
import { HasDocumentToReceivePipe } from './components/has-document-to-receive.pipe';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SpinnerService } from '../../core/services/spinner.service';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { HttpUtils } from '../../shared/utils/http.utils';
import { ExportType } from '../../shared/enums/export-type.enum';
import { InboxApi } from '../../shared/services/api/inbox.api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExportApi } from '../../shared/services/api/export.api';
import { ApplicationVisualizationDialog } from '../application-sent/components/application-visualization-dialog/application-visualization-dialog';
import { VisualizationType } from '../../shared/enums/visualization-type.enum';
import { DokumentApi } from '../../shared/services/api/dokument.api';
import { VisualizationApi } from '../../shared/services/api/visualization.api';
import { KorespondencjaDialogComponent } from '../../shared/components/korespondencja-dialog/korespondencja-dialog.component';
import { TableRowEvent } from '../../shared/models/custom-types';
import { TooltipModule } from 'primeng/tooltip';
import {
  HttpErrorDetails,
  HttpResponseErrorSource,
  HttpResponseService,
} from '../../core/services/http-error-response.service';
import { Paginator } from 'primeng/paginator';

@Component({
  selector: 'app-inbox',
  imports: [
    TableModule,
    TooltipModule,
    DatePipe,
    Button,
    NgClass,
    Card,
    PreviewDataWrapperComponent,
    Checkbox,
    FormsModule,
    InputText,
    DatePicker,
    HasDocumentToReceivePipe,
    ProgressSpinner,
    Paginator,
  ],
  templateUrl: './inbox.component.html',
  styleUrl: './inbox.component.scss',
  providers: [PostApi, DokumentApi, InboxApi, ExportApi, VisualizationApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class InboxComponent extends TableBaseAbstractComponent<Dokument> {
  @ViewChild('table') override tableRef!: Table;
  protected readonly isReceiving = signal<boolean>(false);
  protected selectedMessages: Dokument[] = [];
  protected inboxType = signal<InboxType>(InboxType.ALL);
  protected readonly PreviewDataType = PreviewDataType;
  protected readonly InboxType = InboxType;
  protected readonly DocumentStatus = DocumentStatus;
  readonly #spinnerService = inject(SpinnerService);
  readonly #exportApi = inject(ExportApi);
  readonly #inboxApi = inject(InboxApi);
  readonly #visualizationApi = inject(VisualizationApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);
  protected tableFilters = {
    sender: '',
    sentDate: null,
    certificationDate: null,
  };
  #shouldRunInboxTypeChangeEffect: boolean = false;

  constructor() {
    super();
    this.#initInboxContextEffect();
  }

  #mapInboxToStatus(inboxType: InboxType): DocumentStatus | null {
    if (inboxType === InboxType.TO_RECEIVE) {
      return DocumentStatus.PRZY;
    } else if (inboxType === InboxType.RECEIVED) {
      return DocumentStatus.ODBR;
    }
    return null;
  }

  correspond(dokument: Dokument) {
    this.#dialogService.open(KorespondencjaDialogComponent, {
      header: `Koresponduj w sprawie`,
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '50vw',
      data: {
        dokument: dokument,
      },
      breakpoints: {
        '960px': '70vw',
        '640px': '90vw',
      },
    });
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<Dokument>> => {
    params.sortBy = params?.sortBy ?? 'dataWplywu';
    return this.#inboxApi.getAllInboxDocuments(this.tableRequest());
  };

  protected openShowDocumentModal(dokument: Dokument): void {
    const ref = this.#dialogService.open(DocumentDetailsComponent, {
      closable: true,
      header: `Szczegóły dokumentu`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '60vw',
      inputValues: {
        ['document']: dokument,
        inboxType: this.inboxType(),
      },
      breakpoints: {
        '960px': '70vw',
        '640px': '90vw',
      },
    });

    if (!ref) return;

    ref.onClose
      .pipe(
        filter(
          (result): result is 'archived' | 'received' =>
            result === 'archived' || result === 'received',
        ),
        tap((result) => {
          const inboxType =
            result === 'archived' ? InboxType.ARCHIVED : InboxType.RECEIVED;

          this.inboxType.set(inboxType);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected openShowDocumentModalAndApplicationVisualization(
    dokument: Dokument,
  ): void {
    if (this.isReceiving()) {
      return;
    }

    this.isReceiving.set(true);
    this.#spinnerService.show();

    this.#inboxApi
      .signUPDDigitalSignature(dokument.id)
      .pipe(
        tap((resp: string) => {
          if (resp) {
            this.#httpResponseService.showSuccessMessage(resp);
          }
        }),

        switchMap((resp) => {
          if (!resp) {
            return EMPTY;
          }

          return this.#visualizationApi
            .getVisualization$(dokument.id, VisualizationType.PO)
            .pipe(
              tap((content: string) => this.#openVisualizationDialog(content)),
              catchError((err) => {
                this.#httpResponseService.showErrorMessage(
                  err,
                  'Nie udało się odebrać dokumentu',
                );
                return EMPTY;
              }),
            );
        }),

        tap(() => this.reloadData()),

        catchError((err: HttpErrorResponse) =>
          this.#handleDocumentReceiveError(err, dokument),
        ),

        finalize(() => {
          this.isReceiving.set(false);
          this.#spinnerService.hide();
        }),

        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected isRowSelectable = (e: TableRowEvent) =>
    e.data.statusDokumentu !== DocumentStatus.PRZY;

  protected setInboxContext(inboxType: InboxType) {
    this.inboxType.set(
      this.inboxType() === inboxType ? InboxType.ALL : inboxType,
    );
  }

  protected archiveDocumentDialog(): void {
    if (!this.selectedMessages?.length) {
      return;
    }

    const header =
      this.selectedMessages.length === 1
        ? `Chcesz przenieść wybrany dokument do archiwum? Dokument będzie dostępny po wybraniu filtra „Zarchiwizowane”.`
        : `Chcesz przenieść wybrane dokumenty do archiwum? Dokumenty będą dostępne po wybraniu filtra „Zarchiwizowane”.`;

    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'OK',
      },
    });
    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#archiveDocument()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  protected restoreFromArchiveDocumentDialog(): void {
    if (!this.selectedMessages?.length) {
      return;
    }

    const count = this.selectedMessages.length;

    const header =
      count === 1
        ? `Czy na pewno chcesz cofnąć archiwizację wybranego dokumentu?`
        : `Czy na pewno chcesz cofnąć archiwizację wybranych dokumentów?`;

    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'OK',
      },
    });

    if (!ref) {
      return;
    }

    ref.onClose
      .pipe(
        filter(Boolean),
        tap(() => this.#spinnerService.show()),
        switchMap(() =>
          this.#inboxApi
            .restoreFromArchiveDocuments$(
              new Set(this.selectedMessages.map((value) => value.id)),
            )
            .pipe(
              tap(() => {
                const message =
                  count === 1
                    ? 'Poprawnie przywrócono dokument z archiwum'
                    : 'Poprawnie przywrócono dokumenty z archiwum';
                this.#httpResponseService.showSuccessMessage(message);
              }),
              catchError((err) => {
                const message =
                  count === 1
                    ? 'Wystąpił błąd podczas próby przywrócenia dokumentu z archiwum'
                    : 'Wystąpił błąd podczas próby przywrócenia dokumentów z archiwum';

                this.#httpResponseService.showErrorMessage(err, message);
                return EMPTY;
              }),
              finalize(() => {
                this.selectedMessages = [];
                this.#spinnerService.hide();

                this.inboxType.set(InboxType.RECEIVED);
              }),
            ),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected exportSelected(): void {
    this.#spinnerService.show();
    this.#exportApi
      .exportApplication$(
        new Set(this.selectedMessages.map((value) => value.id)),
        ExportType.APPLICATION,
      )
      .pipe(
        tap((resp) => {
          const contentDisposition = resp.headers.get('content-disposition');
          const fileName = HttpUtils.parseContentDisposition(
            contentDisposition!,
            `plik_${new Date()}.zip`,
          );

          const url = window.URL.createObjectURL(resp.body!);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          window.URL.revokeObjectURL(url);
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas eksportu',
          );
          return EMPTY;
        }),
        finalize(() => {
          this.#spinnerService.hide();
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #handleDocumentReceiveError(
    err: HttpErrorResponse,
    dokument: Dokument,
  ): Observable<never> {
    if (this.#isDocumentAlreadyReceivedError(err)) {
      this.#openAlreadyReceivedDocumentDialog(dokument);
      this.reloadData();
      return EMPTY;
    }

    this.#httpResponseService.showErrorMessage(
      err,
      'Wystąpił problem przy odbieraniu dokumentu',
      HttpResponseErrorSource.MESSAGE_FROM_RESPONSE,
    );
    return EMPTY;
  }

  #isDocumentAlreadyReceivedError(err: HttpErrorResponse): boolean {
    try {
      const errorDetails: HttpErrorDetails | undefined =
        typeof err.error === 'string'
          ? (JSON.parse(err.error) as HttpErrorDetails)
          : (err.error as HttpErrorDetails);

      return (
        err.status === 400 &&
        errorDetails?.errorCode === ErrorCode.DOCUMENT_ALREADY_RECEIVED
      );
    } catch {
      return false;
    }
  }

  #openAlreadyReceivedDocumentDialog(dokument: Dokument): void {
    this.#dialogService.open(DocumentDetailsComponent, {
      closable: true,
      header: 'Szczegóły wiadomości',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      width: '60vw',
      inputValues: { document: dokument },
      breakpoints: {
        '960px': '70vw',
        '640px': '90vw',
      },
    });
  }

  #openVisualizationDialog(content: string): void {
    this.#dialogService.open(ApplicationVisualizationDialog, {
      closable: true,
      header: `Wizualizacja`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        ['content']: content,
      },
      style: {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: '0',
      },
    });
  }

  #archiveDocument(): void {
    this.#spinnerService.show();
    this.#inboxApi
      .archiveDocuments$(
        new Set(this.selectedMessages.map((value) => value.id)),
      )
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie przeniesiono dokumenty do archiwum',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby przeniesienia dokumentów do archiwum',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.selectedMessages = [];
          this.#spinnerService.hide();
          this.inboxType.set(InboxType.ARCHIVED);
        }),
      )
      .subscribe();
  }

  #initInboxContextEffect(): void {
    effect(() => {
      const type = this.inboxType();

      if (this.#shouldRunInboxTypeChangeEffect) {
        this.resetFilters();
        const archived = type === InboxType.ARCHIVED;
        const status = archived ? null : this.#mapInboxToStatus(type);

        this.onPrimeFilterChange(archived, 'archiwalne');
        this.onPrimeFilterChange(status, 'statusDokumentu');

        this.selectedMessages = [];
      } else {
        this.#shouldRunInboxTypeChangeEffect = true;
      }
    });
  }

  clearFilters(): void {
    this.tableFilters = {
      sender: '',
      sentDate: null,
      certificationDate: null,
    };
    this.resetFilters();
  }
}
